// PaymentCallback.tsx

import { useEffect, useRef, useState } from "react";
import {
  useSearchParams,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { motion } from "framer-motion";
import { MdCheckCircle, MdError } from "react-icons/md";

import { useAppDispatch } from "../redux/hooks";
import { clearCart } from "../redux/slices/cartSlice";
import { request } from "../api/request";
import ENDPOINTS from "../api/endpoints";
import { isTemplateDomain } from "../utils/domainUtils";

type Status = "idle" | "loading" | "success" | "error";

export const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const {
    restaurantId: stripeRestaurantId,
    orderId: stripeOrderId,
  } = useParams<{
    restaurantId?: string;
    orderId?: string;
  }>();

  const [status, setStatus] = useState<Status>("idle");
  const processedRef = useRef(false);

  const pathname = location.pathname.toLowerCase();

  const isPaymentSuccess = pathname.includes("payment-success");
  const isPaymentFailed = pathname.includes("payment-failed");
  const isPaymentCallback = isPaymentSuccess || isPaymentFailed;

  const isStripeCallback =
    pathname.includes("/menu/stripe/payment-success/") ||
    pathname.includes("/menu/stripe/payment-failed/");

  const orderId = isStripeCallback
    ? stripeOrderId
    : searchParams.get("orderId");

  const restaurantId = isStripeCallback
    ? stripeRestaurantId
    : searchParams.get("restaurantId");

  const validOrderId =
    typeof orderId === "string" && orderId.trim().length > 0
      ? orderId.trim()
      : null;

  const validRestaurantId =
    typeof restaurantId === "string" && restaurantId.trim().length > 0
      ? restaurantId.trim()
      : null;

  const scheduleData = localStorage.getItem("pendingOrderSchedule");

  let schedule: any = null;

  try {
    schedule = scheduleData ? JSON.parse(scheduleData) : null;
  } catch (error) {
    console.error(
      "[Payment Callback] Failed to parse pendingOrderSchedule:",
      error
    );
    schedule = null;
  }

  const handleClose = () => {
    localStorage.removeItem("pendingOrderSchedule");
    setStatus("idle");

    const isTemplate = isTemplateDomain();

    const menuPath = isTemplate
      ? validRestaurantId
        ? `/${validRestaurantId}/menu`
        : "/menu"
      : "/menu";

    navigate(menuPath, { replace: true });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Date not available";

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return "Date not available";
    }

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return "Time not available";

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return "Time not available";
    }

    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  useEffect(() => {
    if (!isPaymentCallback || processedRef.current) {
      return;
    }

    processedRef.current = true;

    const paymentStatus = isPaymentSuccess ? "paid" : "unpaid";

    if (!validOrderId) {
      console.log(
        "[Payment Callback] No orderId found. Skipping payment status API.",
        {
          pathname: location.pathname,
          paymentStatus,
        }
      );

      setStatus(isPaymentSuccess ? "success" : "error");
      return;
    }

    if (!validRestaurantId) {
      console.error(
        "[Payment Callback] restaurantId is missing. Skipping payment status API.",
        {
          orderId: validOrderId,
          pathname: location.pathname,
        }
      );

      setStatus(isPaymentSuccess ? "success" : "error");
      return;
    }

    let mounted = true;

    setStatus("loading");

    const payload = isStripeCallback
      ? {
          orderId: validOrderId,
          paymentStatus,
        }
      : {
          posOrderId: validOrderId,
          paymentStatus,
        };

    console.log("[Payment Callback] Calling payment status API:", {
      orderId: validOrderId,
      restaurantId: validRestaurantId,
      paymentStatus,
      isStripeCallback,
      pathname: location.pathname,
      payload,
    });

    const updatePaymentStatus = async () => {
      try {
        const response: any = await request({
          url: `${ENDPOINTS.API_V2_BASE_URL}${ENDPOINTS.PAYMENT_STATUS_UPDATE_API_PATH}`,
          method: "POST",
          headers: {
            "restaurant-id": validRestaurantId,
          },
          data: payload,
        });

        if (!mounted) return;

        console.log(
          "[Payment Callback] Payment status API response:",
          response
        );

        if (response?.data?.success) {
          if (paymentStatus === "paid") {
            dispatch(clearCart());
          }

          setStatus(isPaymentSuccess ? "success" : "error");
          return;
        }

        console.error(
          "[Payment Callback] API returned unsuccessful response:",
          response
        );

        setStatus("error");
      } catch (error) {
        console.error(
          "[Payment Callback] Payment status update failed:",
          error
        );

        if (mounted) {
          setStatus("error");
        }
      }
    };

    updatePaymentStatus();

    return () => {
      mounted = false;
    };
  }, [
    isPaymentCallback,
    isPaymentSuccess,
    isPaymentFailed,
    validOrderId,
    validRestaurantId,
    isStripeCallback,
    location.pathname,
    dispatch,
  ]);

  if (!isPaymentCallback) {
    return null;
  }

  if (status === "loading") {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-loading-title"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="mx-4 w-full max-w-sm rounded-2xl bg-white shadow-2xl sm:max-w-md"
        >
          <div className="p-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>

            <h3
              id="payment-loading-title"
              className="mb-2 text-lg font-semibold text-gray-900 sm:text-xl"
            >
              Processing Payment
            </h3>

            <p className="text-sm text-gray-600 sm:text-base">
              Please wait while we confirm your payment...
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-success-title"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="mx-4 w-full max-w-sm rounded-2xl bg-white shadow-2xl sm:max-w-md"
        >
          <div className="p-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 shadow-md">
              <MdCheckCircle
                className="h-8 w-8 text-green-600"
                aria-hidden="true"
              />
            </div>

            <h3
              id="payment-success-title"
              className="mb-3 text-xl font-semibold text-gray-900"
            >
              Order Confirmed
            </h3>

            <p className="mb-2 text-gray-600">Thank you!</p>

            <p className="mb-4 text-gray-600">
              Your order has been placed successfully and is being prepared.
            </p>

            {schedule?.orderNumber && (
              <p className="mb-6 text-sm text-gray-600">
                Order ID: #ORD-{schedule.orderNumber}
              </p>
            )}

            {schedule?.scheduledFor &&
              schedule.fulfillmentType === "pickup" && (
                <div className="mb-6">
                  <div className="mb-4 border-t border-gray-300" />

                  <div className="mb-3 flex items-center justify-center">
                    <span className="mr-2 text-lg">🛍️</span>
                    <p className="text-base font-semibold text-gray-900">
                      Pickup Schedule
                    </p>
                  </div>

                  <div className="mb-2 flex items-center justify-center">
                    <span className="mr-2 text-base">📅</span>
                    <p className="text-gray-800">
                      {formatDate(schedule.scheduledFor)}
                    </p>
                  </div>

                  <div className="mb-4 flex items-center justify-center">
                    <span className="mr-2 text-base">🕦</span>
                    <p className="text-gray-800">
                      {formatTime(schedule.scheduledFor)}
                    </p>
                  </div>

                  <p className="mt-3 text-sm text-gray-600">
                    Please arrive at your selected pickup location a few
                    minutes before your scheduled time.
                  </p>

                  <div className="mt-4 border-t border-gray-300" />
                </div>
              )}

            <button
              type="button"
              onClick={handleClose}
              aria-label="Continue shopping"
              className="rounded-lg px-6 py-3 font-semibold text-white transition-colors focus:outline-none"
              style={{ backgroundColor: "#EF4421" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#D13A1C";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#EF4421";
              }}
            >
              Continue Shopping
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-error-title"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="mx-4 w-full max-w-sm rounded-2xl bg-white shadow-2xl sm:max-w-md"
        >
          <div className="p-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 shadow-md">
              <MdError
                className="h-8 w-8 text-red-600"
                aria-hidden="true"
              />
            </div>

            <h3
              id="payment-error-title"
              className="mb-3 text-xl font-semibold text-gray-900"
            >
              Order Failed
            </h3>

            <p className="mb-6 text-gray-600">
              We couldn't process your order. Please try again or contact
              support if you need assistance.
            </p>

            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleClose}
                aria-label="Try again"
                className="rounded-lg px-6 py-3 font-semibold text-white transition-colors focus:outline-none"
                style={{ backgroundColor: "#EF4421" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#D13A1C";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#EF4421";
                }}
              >
                Try Again
              </button>

              <button
                type="button"
                onClick={handleClose}
                className="rounded-lg bg-gray-200 px-6 py-3 font-semibold text-gray-800 transition-colors hover:bg-gray-300 focus:outline-none"
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return null;
};

export default PaymentCallback;
